/**
 * ContactFieldset - Shared fieldset molecule for selecting or creating
 * an on-site contact for a work order.
 *
 * Provides three contact selection modes via radio buttons:
 *	 1. Select User	  -- pick from the platform users of the selected customer,
 *						 via server-search typeahead; auto-fills name/phone/email
 *	 2. Existing Contact -- pick from the people already on file for the selected
 *						 customer: saved contact records PLUS the platform users
 *						 associated with that company. Eager-loaded since the list
 *						 is scoped to one company. Auto-fills fields.
 *	 3. New Contact	  -- manual entry of first name, last name, title, phone, email
 *
 * Mode 2 used to list saved contact records only, which meant it read as empty
 * ("No contacts on file for this customer yet") for practically every customer
 * — standalone contact rows only exist once somebody has explicitly created
 * one. Folding in the customer's users makes the primary flow (pick a customer,
 * then pick their person) actually work on day one. The /users request carries
 * company_id, and the endpoint applies the requester's own company scope on top
 * of it, so this can never widen who a user is able to see.
 *
 * Quick add: given `quickAdd`, the Select User and Existing Contact pickers
 * offer "+ Quick Add User" (a link under the picker and an action row inside
 * its dropdown). The person created there — defaulting to the job's customer
 * as their business — is selected as the contact straight away.
 *
 * Phone inputs are auto-formatted via formatPhone for consistent (###) ###-#### display.
 *
 * Used by both the work order and inspection form types in the JobForm organism.
 *
 * The "Select User" dropdown is server-search via AsyncSearchableSelect so it
 * scales to thousands of users without preloading. Its search carries the
 * selected company_id, the same scope the "Existing Contact" list uses: the
 * on-site contact for a job is somebody at that customer, so offering every
 * user on the platform made the picker a wall of unrelated names and let a
 * requester attach a person from another customer to the job. Like the
 * eager-loaded list above, the endpoint applies the requester's own company
 * scope on top of the filter, so this only ever narrows what is visible.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../../../services/api';
import { contactService } from '../../../services/contactService';
import { SearchableSelect } from '../SearchableSelect';
import { AsyncSearchableSelect, AsyncSearchableSelectOption } from '../AsyncSearchableSelect';
import { QuickAddUserLink, useQuickAddFooterAction } from '../QuickAddUserLink';
import { splitName } from '../../../utils/quickAddUser';
import type { QuickAddedPersonRef, QuickAddOptions } from '../../../utils/quickAddUser';
import { formatPhone } from '../../../utils/formatPhone';
import { formatRoleLabel } from '../../../utils/formatLabel';
import { dedupeAndSort } from '../../../utils/dedupeAndSort';

/* ============================================
 * CONSTANTS
 * ============================================ */

/* The "Existing Contact" dropdown mixes two record types whose ids are drawn
   from different tables — contact 12 and user 12 both exist. Option values are
   namespaced with these prefixes so the change handler can tell which of the
   two a selection refers to. */
const CONTACT_OPTION_PREFIX = 'contact:';
const USER_OPTION_PREFIX = 'user:';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/** The three modes for selecting/creating an on-site contact */
export type ContactMode = 'select_user' | 'existing_contact' | 'new_contact';

/** Minimal user record for the "Select User" dropdown */
interface DropdownUser {
	id: string | number;
	first_name: string;
	last_name: string;
	email: string;
	phone?: string;
	/** Used as the qualifier in the "Existing Contact" label (users have no title column) */
	role?: string;
}

/** Company contact record for the "Existing Contact" dropdown */
interface DropdownContact {
	id: string | number;
	first_name: string;
	last_name: string;
	full_name: string;
	phone: string | null;
	email: string | null;
	title: string | null;
	/** Platform users linked to this contact — used to de-duplicate the merged list */
	users?: Array<{ id: string | number }>;
}

/** Props for the ContactFieldset molecule */
export interface ContactFieldsetProps {
	/** Currently selected company ID -- required for "Existing Contact" mode */
	companyId: string;
	contactMode: ContactMode;
	onContactModeChange: (mode: ContactMode) => void;
	contactName: string;
	onContactNameChange: (name: string) => void;
	contactPhone: string;
	onContactPhoneChange: (phone: string) => void;
	contactEmail: string;
	onContactEmailChange: (email: string) => void;
	newContactFirstName: string;
	onNewContactFirstNameChange: (name: string) => void;
	newContactLastName: string;
	onNewContactLastNameChange: (name: string) => void;
	newContactTitle: string;
	onNewContactTitleChange: (title: string) => void;
	contactUserId: string;
	onContactUserIdChange: (id: string) => void;
	selectedContactId: string;
	onSelectedContactIdChange: (id: string) => void;
	/**
	 * Persist the contact currently typed into New Contact mode, immediately,
	 * and return its new id. Without this the contact was only created when the
	 * whole work order was submitted — so abandoning or failing the form lost
	 * it, and it never showed up in the Existing Contact list.
	 */
	onSaveNewContact?: () => Promise<string | null>;
	/** Offer "+ Quick Add User" on the Select User / Existing Contact pickers;
	 *  the new person becomes the contact. Their business defaults to the
	 *  selected customer (`companyId` above) unless `quickAdd.companyId` says otherwise. */
	quickAdd?: QuickAddOptions;
	labelClass: string;
	inputClass: string;
	normalBorder: string;
	/** Optional extra classes appended to the root element. */
	className?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const ContactFieldset: React.FC<ContactFieldsetProps> = ({
	companyId,
	contactMode,
	onContactModeChange,
	contactName,
	onContactNameChange,
	contactPhone,
	onContactPhoneChange,
	contactEmail,
	onContactEmailChange,
	newContactFirstName,
	onNewContactFirstNameChange,
	newContactLastName,
	onNewContactLastNameChange,
	newContactTitle,
	onNewContactTitleChange,
	contactUserId,
	onContactUserIdChange,
	selectedContactId,
	onSelectedContactIdChange,
	onSaveNewContact,
	quickAdd,
	labelClass,
	inputClass,
	normalBorder,
	className = '',
}) => {
	/* Cache of users seen via search or single-id resolve. Lets the auto-fill
	   logic look up phone/email by id without a second round trip, and lets the
	   selected-label render correctly on edit-mode mount. */
	const [userCache, setUserCache] = useState<Record<string, DropdownUser>>({});
	const [companyContacts, setCompanyContacts] = useState<DropdownContact[]>([]);
	/* Platform users associated with the selected company — the other half of
	   the "Existing Contact" list. */
	const [companyUsers, setCompanyUsers] = useState<DropdownUser[]>([]);
	const [isLoadingPeople, setIsLoadingPeople] = useState(false);
	/* Bumped after a contact is saved to force the company-people effect to
	   re-run. The list is fetched once per company; without this the dropdown
	   kept serving the pre-save snapshot and a just-created contact looked
	   like it had never been saved. */
	const [contactsRefreshKey, setContactsRefreshKey] = useState(0);
	const [isSavingContact, setIsSavingContact] = useState(false);
	const [saveContactError, setSaveContactError] = useState<string | null>(null);

	/* If contactUserId is set on mount but not in the cache, resolve it via
	   /users/{id} so the selected name shows immediately. */
	useEffect(() => {
		if (!contactUserId || contactUserId in userCache) return;

		let cancelled = false;
		(async () => {
			try {
				const res = await api.get<any>(`/users/${contactUserId}`);
				const u: DropdownUser | undefined = res.data?.data ?? res.data;
				if (!cancelled && u && u.id != null) {
					setUserCache((prev) => ({ ...prev, [String(u.id)]: u }));
				}
			} catch {
				/* Display will fall back to the raw id */
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [contactUserId, userCache]);

	/**
	 * Fetches everyone available for "Existing Contact" on the selected
	 * company: the saved contact records and the platform users associated
	 * with that company.
	 *
	 * The two requests are settled independently rather than via Promise.all
	 * so one failing does not blank the other — a user without permission to
	 * list users should still see the customer's saved contacts, and vice
	 * versa.
	 */
	useEffect(() => {
		if (!companyId) {
			setCompanyContacts([]);
			setCompanyUsers([]);
			return;
		}

		let cancelled = false;

		const loadCompanyPeople = async () => {
			setIsLoadingPeople(true);
			const [contactsResult, usersResult] = await Promise.allSettled([
				contactService.getByCompany(companyId),
				/* status=active hides deactivated accounts, matching the
				   active-only default on the contacts endpoint. per_page
				   mirrors getByCompany: /users paginates at 25, which would
				   silently truncate the roster for any larger customer. */
				api.get<any>('/users', { company_id: companyId, status: 'active', per_page: '999' }),
			]);

			if (cancelled) return;

			if (contactsResult.status === 'fulfilled') {
				const res = contactsResult.value;
				setCompanyContacts(Array.isArray(res.data) ? res.data : (res.data?.data ?? []));
			} else {
				console.error('[ContactFieldset] Failed to load company contacts:', contactsResult.reason);
				setCompanyContacts([]);
			}

			if (usersResult.status === 'fulfilled') {
				const res = usersResult.value;
				const users: DropdownUser[] = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
				setCompanyUsers(users);
				/* Seed the shared cache so the mount-time /users/{id} resolve
				   effect stays quiet for anyone picked out of this list. */
				setUserCache((prev) => {
					const next = { ...prev };
					for (const u of users) {
						next[String(u.id)] = u;
					}
					return next;
				});
			} else {
				console.error('[ContactFieldset] Failed to load company users:', usersResult.reason);
				setCompanyUsers([]);
			}

			setIsLoadingPeople(false);
		};

		loadCompanyPeople();

		return () => {
			cancelled = true;
		};
	}, [companyId, contactsRefreshKey]);

	/**
	 * Save the typed-in contact now, then switch to Existing Contact with it
	 * selected. This is what makes "add a contact while writing a work order"
	 * actually add a contact: the record is persisted immediately rather than
	 * at form submit, so it survives an abandoned form and appears in the
	 * customer's contact list straight away.
	 */
	const handleSaveNewContact = useCallback(async () => {
		if (!onSaveNewContact) return;
		setSaveContactError(null);
		setIsSavingContact(true);
		try {
			const newId = await onSaveNewContact();
			if (newId) {
				setContactsRefreshKey((k) => k + 1);
				onSelectedContactIdChange(newId);
				onContactModeChange('existing_contact');
			}
		} catch (err) {
			setSaveContactError(err instanceof Error ? err.message : 'Could not save this contact. Please try again.');
		} finally {
			setIsSavingContact(false);
		}
	}, [onSaveNewContact, onSelectedContactIdChange, onContactModeChange]);

	/* Server-side search for the selected customer's users. Caches each result
	   so the auto-fill handler can look up phone/email without another request.
	   Results are alphabetized A-Z by display name via dedupeAndSort since the
	   API does not guarantee an order.

	   company_id scopes the search to the customer picked above — /users
	   matches it against the company_users junction as well as the legacy
	   users.company_id column, so people linked to the customer as a secondary
	   company are included. status=active mirrors the Existing Contact list so
	   deactivated accounts stay out of both. The dropdown is disabled without a
	   company, so this never fires unscoped. */
	const searchUsers = useCallback(async (search: string): Promise<AsyncSearchableSelectOption[]> => {
		if (!companyId) return [];
		const res = await api.get<any>('/users', { search, company_id: companyId, status: 'active' });
		const data: DropdownUser[] = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
		setUserCache((prev) => {
			const next = { ...prev };
			for (const u of data) {
				next[String(u.id)] = u;
			}
			return next;
		});
		return dedupeAndSort(
			data.map((u) => ({
				value: String(u.id),
				label: `${u.first_name} ${u.last_name}`,
			}))
		);
	}, [companyId]);

	/** Handles mode change -- resets all contact fields to prevent stale data */
	const handleModeChange = useCallback((mode: ContactMode) => {
		onContactModeChange(mode);
		onContactUserIdChange('');
		onSelectedContactIdChange('');
		onContactNameChange('');
		onContactPhoneChange('');
		onContactEmailChange('');
		onNewContactFirstNameChange('');
		onNewContactLastNameChange('');
		onNewContactTitleChange('');
	}, [onContactModeChange, onContactUserIdChange, onSelectedContactIdChange, onContactNameChange, onContactPhoneChange, onContactEmailChange, onNewContactFirstNameChange, onNewContactLastNameChange, onNewContactTitleChange]);

	/** Auto-fills contact fields when a system user is selected */
	const handleUserSelect = useCallback((userId: string) => {
		onContactUserIdChange(userId);
		if (!userId) {
			onContactNameChange('');
			onContactPhoneChange('');
			onContactEmailChange('');
			return;
		}
		const cached = userCache[userId];
		if (cached) {
			onContactNameChange(`${cached.first_name} ${cached.last_name}`);
			onContactPhoneChange(cached.phone || '');
			onContactEmailChange(cached.email || '');
		}
	}, [userCache, onContactUserIdChange, onContactNameChange, onContactPhoneChange, onContactEmailChange]);

	/**
	 * The merged "Existing Contact" option list — saved contact records plus
	 * the company's platform users, each namespaced by source.
	 *
	 * Labels carry a qualifier (the contact's job title, the user's role) so
	 * similar names are tellable apart while picking.
	 */
	const existingContactOptions = useMemo(() => {
		const contactOptions = companyContacts.map((c) => {
			const name = c.full_name || `${c.first_name} ${c.last_name}`;
			return { value: `${CONTACT_OPTION_PREFIX}${c.id}`, label: c.title ? `${name} — ${c.title}` : name };
		});

		/* A person who is both a saved contact and a platform user would
		   otherwise appear twice. The contact row wins because it already
		   carries the contact_id the job will reference. Matching happens on
		   the contact_users link first, then on email as a fallback for
		   contacts that were typed in by hand rather than linked. */
		const linkedUserIds = new Set<string>();
		const contactEmails = new Set<string>();
		for (const c of companyContacts) {
			for (const u of c.users ?? []) linkedUserIds.add(String(u.id));
			if (c.email) contactEmails.add(c.email.trim().toLowerCase());
		}

		const userOptions = companyUsers
			.filter((u) => !linkedUserIds.has(String(u.id)) && !(u.email && contactEmails.has(u.email.trim().toLowerCase())))
			.map((u) => {
				const name = `${u.first_name} ${u.last_name}`.trim();
				return { value: `${USER_OPTION_PREFIX}${u.id}`, label: u.role ? `${name} — ${formatRoleLabel(u.role)}` : name };
			});

		return [...contactOptions, ...userOptions];
	}, [companyContacts, companyUsers]);

	/* Which merged option is currently selected. Only one of the two ids is
	   ever set at a time — the handler below clears the other. */
	const existingContactValue = selectedContactId
		? `${CONTACT_OPTION_PREFIX}${selectedContactId}`
		: contactUserId
			? `${USER_OPTION_PREFIX}${contactUserId}`
			: '';

	/**
	 * Auto-fills contact fields when a row is picked out of the merged list.
	 *
	 * Picking a saved contact sets selectedContactId (the job references it
	 * directly). Picking a user sets contactUserId instead — JobForm then
	 * find-or-creates that user's contact record for the company at submit
	 * time, exactly as "Select User" mode does, so no contact row is written
	 * for a job that is never submitted.
	 */
	const handleExistingContactSelect = useCallback((optionValue: string) => {
		/* Cleared selection — drop both ids and the auto-filled snapshot. */
		if (!optionValue) {
			onSelectedContactIdChange('');
			onContactUserIdChange('');
			onContactNameChange('');
			onContactPhoneChange('');
			onContactEmailChange('');
			return;
		}

		if (optionValue.startsWith(USER_OPTION_PREFIX)) {
			const userId = optionValue.slice(USER_OPTION_PREFIX.length);
			onSelectedContactIdChange('');
			onContactUserIdChange(userId);
			const selected = companyUsers.find((u) => String(u.id) === userId);
			if (selected) {
				onContactNameChange(`${selected.first_name} ${selected.last_name}`.trim());
				onContactPhoneChange(selected.phone || '');
				onContactEmailChange(selected.email || '');
			}
			return;
		}

		const contactId = optionValue.slice(CONTACT_OPTION_PREFIX.length);
		onContactUserIdChange('');
		onSelectedContactIdChange(contactId);
		const selected = companyContacts.find((c) => String(c.id) === contactId);
		if (selected) {
			onContactNameChange(selected.full_name || `${selected.first_name} ${selected.last_name}`);
			onContactPhoneChange(selected.phone || '');
			onContactEmailChange(selected.email || '');
		}
	}, [companyContacts, companyUsers, onSelectedContactIdChange, onContactUserIdChange, onContactNameChange, onContactPhoneChange, onContactEmailChange]);

	/* ---- Quick add ----
	   The first person the modal saves becomes the contact: cached so the
	   Select User input can show their name, the read-only details filled in
	   from what was typed, and the Existing Contact list re-fetched so the
	   merged option (and its label) exists for them too. */
	const handleQuickAddedContact = useCallback((people: QuickAddedPersonRef[]) => {
		const person = people[0];
		if (!person) return;
		const { firstName, lastName } = splitName(person.name);
		setUserCache((prev) => ({ ...prev, [person.id]: { id: person.id, first_name: firstName, last_name: lastName, email: person.email ?? '', phone: person.phone ?? undefined } }));
		onSelectedContactIdChange('');
		onContactUserIdChange(person.id);
		onContactNameChange(person.name);
		onContactPhoneChange(person.phone ? formatPhone(person.phone) : '');
		onContactEmailChange(person.email ?? '');
		setContactsRefreshKey((k) => k + 1);
	}, [onSelectedContactIdChange, onContactUserIdChange, onContactNameChange, onContactPhoneChange, onContactEmailChange]);

	/* Only offered once a customer is picked — the new person needs a business,
	   and both pickers are disabled until then anyway. */
	const quickAddCompanyId = quickAdd ? (quickAdd.companyId ?? companyId) : null;
	const contactFooterAction = useQuickAddFooterAction(quickAdd && companyId ? { defaultCompanyId: quickAddCompanyId, onCreated: handleQuickAddedContact, single: true } : null);
	const contactQuickAddLink = quickAdd && companyId
		? <QuickAddUserLink defaultCompanyId={quickAddCompanyId} onCreated={handleQuickAddedContact} single className="mt-4px" />
		: null;

	/* Display label for the currently-selected user, used by the typeahead's
	   closed-state input value */
	const selectedUserLabel = contactUserId && userCache[contactUserId]
		? `${userCache[contactUserId].first_name} ${userCache[contactUserId].last_name}`
		: '';

	return (
		<div className={`contact-fieldset space-y-16px ${className}`} data-ref="molecule-contact-fieldset">
			{/* Contact mode radio selector -- three modes */}
			<div className="contact-fieldset__modes flex flex-wrap gap-16px">
				<label className="flex items-center gap-8px cursor-pointer min-h-44px">
					<input type="radio" name="contactMode" value="select_user" checked={contactMode === 'select_user'} onChange={() => handleModeChange('select_user')} className="w-16px h-16px text-brand-primary focus:ring-brand-primary" />
					<span className="text-14px font-sans font-medium text-gray-700">Select User</span>
				</label>
				<label className="flex items-center gap-8px cursor-pointer min-h-44px">
					<input type="radio" name="contactMode" value="existing_contact" checked={contactMode === 'existing_contact'} onChange={() => handleModeChange('existing_contact')} className="w-16px h-16px text-brand-primary focus:ring-brand-primary" />
					<span className="text-14px font-sans font-medium text-gray-700">Existing Contact</span>
				</label>
				<label className="flex items-center gap-8px cursor-pointer min-h-44px">
					<input type="radio" name="contactMode" value="new_contact" checked={contactMode === 'new_contact'} onChange={() => handleModeChange('new_contact')} className="w-16px h-16px text-brand-primary focus:ring-brand-primary" />
					<span className="text-14px font-sans font-medium text-gray-700">New Contact</span>
				</label>
			</div>

			{/* Mode 1: Select User -- server-search typeahead */}
			{contactMode === 'select_user' && (
				<div>
					<label htmlFor="contactUserId" className={labelClass}>Select a User</label>
					<AsyncSearchableSelect
						id="contactUserId"
						value={contactUserId}
						selectedLabel={selectedUserLabel}
						onChange={(id) => handleUserSelect(id)}
						fetchOptions={searchUsers}
						placeholder={companyId ? 'Search the customer\'s users...' : 'Select a customer first'}
						disabled={!companyId}
						emptyMessage="No users found for this customer."
						footerAction={contactFooterAction}
					/>
					{contactQuickAddLink}
				</div>
			)}

			{/* Mode 2: Existing Contact -- searchable dropdown of everyone on file
			    for the selected customer (saved contacts + the company's users).
			    Labels carry a qualifier ("Jane Roe — Site Manager") so similar
			    names are tellable apart while picking. */}
			{contactMode === 'existing_contact' && (
				<div>
					<label htmlFor="selectedContactId" className={labelClass}>Select a Contact</label>
					<SearchableSelect
						id="selectedContactId"
						options={existingContactOptions}
						value={existingContactValue}
						onChange={handleExistingContactSelect}
						placeholder={!companyId ? 'Select a customer first' : isLoadingPeople ? 'Loading contacts...' : 'Search the customer\'s contacts and users...'}
						disabled={!companyId || isLoadingPeople}
						footerAction={contactFooterAction}
					/>
					{contactQuickAddLink}
					{companyId && !isLoadingPeople && existingContactOptions.length === 0 && (
						<p className="mt-4px text-12px text-brand-text-muted">No contacts or users on file for this customer yet — use New Contact to add one.</p>
					)}
				</div>
			)}

			{/* Mode 3: New Contact -- manual entry fields */}
			{contactMode === 'new_contact' && (
				<div className="space-y-16px">
					{/* First Name and Last Name on the same row */}
					<div className="grid grid-cols-1 tablet:grid-cols-2 gap-16px">
						<div>
							<label htmlFor="newContactFirstName" className={labelClass}>First Name</label>
							<input id="newContactFirstName" type="text" value={newContactFirstName} onChange={(e) => onNewContactFirstNameChange(e.target.value)} className={`${inputClass} ${normalBorder}`} placeholder="John" />
						</div>
						<div>
							<label htmlFor="newContactLastName" className={labelClass}>Last Name</label>
							<input id="newContactLastName" type="text" value={newContactLastName} onChange={(e) => onNewContactLastNameChange(e.target.value)} className={`${inputClass} ${normalBorder}`} placeholder="Doe" />
						</div>
					</div>

					{/* Phone and Email on the same row -- phone uses formatPhone */}
					<div className="grid grid-cols-1 tablet:grid-cols-2 gap-16px">
						<div>
							<label htmlFor="contactPhone" className={labelClass}>Phone</label>
							<input id="contactPhone" type="tel" value={contactPhone} onChange={(e) => onContactPhoneChange(formatPhone(e.target.value))} className={`${inputClass} ${normalBorder}`} placeholder="(555) 123-4567" />
						</div>
						<div>
							<label htmlFor="contactEmail" className={labelClass}>Email</label>
							<input id="contactEmail" type="email" value={contactEmail} onChange={(e) => onContactEmailChange(e.target.value)} className={`${inputClass} ${normalBorder}`} placeholder="john@example.com" />
						</div>
					</div>

					{/* Title field -- job title like "Site Manager" */}
					<div className="max-w-sm">
						<label htmlFor="newContactTitle" className={labelClass}>Title</label>
						<input id="newContactTitle" type="text" value={newContactTitle} onChange={(e) => onNewContactTitleChange(e.target.value)} className={`${inputClass} ${normalBorder}`} placeholder="e.g., Site Manager" />
					</div>

					{/* Save the contact to the customer's contact list NOW rather than
					    waiting for the work order to be submitted. */}
					{onSaveNewContact && (
						<div className="contact-fieldset__save-contact flex flex-col gap-4px">
							<div className="flex flex-col tablet:flex-row tablet:items-center gap-8px">
								<button
									type="button"
									onClick={handleSaveNewContact}
									disabled={isSavingContact || !companyId || (!newContactFirstName.trim() && !newContactLastName.trim())}
									className="inline-flex items-center justify-center gap-6px px-16px py-10px text-14px font-sans font-medium text-white bg-brand-primary hover:bg-brand-primary-dark rounded-md min-h-44px transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
								>
									{isSavingContact ? 'Saving…' : 'Save contact to customer'}
								</button>
								<p className="text-12px font-sans text-brand-text-muted">
									Adds this person to the customer&apos;s contact list so they can be reused on future jobs.
								</p>
							</div>
							{saveContactError && (
								<p role="alert" className="text-12px font-sans text-status-error">{saveContactError}</p>
							)}
						</div>
					)}
				</div>
			)}

			{/* Auto-filled contact display (Modes 1 & 2) -- read-only verification */}
			{(contactMode === 'select_user' || contactMode === 'existing_contact') && contactName && (
				<div className="grid grid-cols-1 tablet:grid-cols-3 gap-16px pt-8px border-t border-gray-200">
					<div>
						<label className={labelClass}>Contact Name</label>
						<input type="text" value={contactName} readOnly className={`${inputClass} ${normalBorder} bg-gray-50 cursor-not-allowed`} />
					</div>
					<div>
						<label className={labelClass}>Contact Phone</label>
						<input type="tel" value={contactPhone} readOnly className={`${inputClass} ${normalBorder} bg-gray-50 cursor-not-allowed`} />
					</div>
					<div>
						<label className={labelClass}>Contact Email</label>
						<input type="email" value={contactEmail} readOnly className={`${inputClass} ${normalBorder} bg-gray-50 cursor-not-allowed`} />
					</div>
				</div>
			)}
		</div>
	);
};

export default ContactFieldset;
